/**
 * Diálogo de detalle de log de auditoría
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, CheckCircle, Error, Code } from '@mui/icons-material';
import { useFetchAuditoriaLogDetalle, useVerificarIntegridad } from '@/shared/actions/erp/auditoria.actions';
import { formatFechaLog, getAccionColor, getMetodoHttpColor } from '@/shared/actions/erp/auditoria.actions';
import { JsonViewer } from '@textea/json-viewer';

// Componente de esquina decorativa (solo líneas en L)
const CornerMark = ({ position }: { position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }) => {
  const size = 30;
  const thickness = 3;

  const positions: Record<string, object> = {
    'top-left': { top: 8, left: 8, borderTop: `${thickness}px solid`, borderLeft: `${thickness}px solid` },
    'top-right': { top: 8, right: 8, borderTop: `${thickness}px solid`, borderRight: `${thickness}px solid` },
    'bottom-left': { bottom: 8, left: 8, borderBottom: `${thickness}px solid`, borderLeft: `${thickness}px solid` },
    'bottom-right': { bottom: 8, right: 8, borderBottom: `${thickness}px solid`, borderRight: `${thickness}px solid` },
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        borderColor: 'primary.main',
        opacity: 0.5,
        pointerEvents: 'none',
        zIndex: 1,
        ...positions[position],
      }}
    />
  );
};

interface Props {
  logId: string;
  open: boolean;
  onClose: () => void;
}

export const AuditoriaDetalle = ({ logId, open, onClose }: Props) => {
  const { data: log, isLoading } = useFetchAuditoriaLogDetalle(logId, open);
  const verificarMutation = useVerificarIntegridad();

  const handleVerificarIntegridad = async () => {
    await verificarMutation.mutateAsync(logId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Detalle de Log de Auditoría</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ position: 'relative', minHeight: 200 }}>
        {/* Marcas de esquina estilo documento oficial */}
        <CornerMark position="top-left" />
        <CornerMark position="top-right" />
        <CornerMark position="bottom-left" />
        <CornerMark position="bottom-right" />

        <Box sx={{ position: 'relative', zIndex: 0, pt: 2, pb: 2 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : log ? (
          <Box>
            {/* Información General */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Información General
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Fecha
                  </Typography>
                  <Typography variant="body2">{formatFechaLog(log.fecha)}</Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Usuario
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {log.usuario.nombre_completo || log.usuario.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {log.usuario.email}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Módulo
                  </Typography>
                  <Box mt={0.5}>
                    <Chip label={log.modulo} size="small" variant="outlined" />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Acción
                  </Typography>
                  <Box mt={0.5}>
                    <Chip label={log.accion} size="small" color={getAccionColor(log.accion)} />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Método HTTP
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={log.metodo_http}
                      size="small"
                      color={getMetodoHttpColor(log.metodo_http)}
                      variant="outlined"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {log.ip_address}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Estado HTTP
                  </Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={log.estado_http}
                      size="small"
                      color={log.estado_http >= 200 && log.estado_http < 300 ? 'success' : 'error'}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Endpoint
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: 'action.hover',
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {log.endpoint}
                  </Typography>
                </Grid>

                {log.registro_id && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Registro ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {log.registro_id}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Tipo de Registro
                      </Typography>
                      <Typography variant="body2">{log.registro_tipo}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>

            {/* Integridad */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Verificación de Integridad</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleVerificarIntegridad}
                  disabled={verificarMutation.isPending}
                >
                  {verificarMutation.isPending ? 'Verificando...' : 'Verificar'}
                </Button>
              </Box>

              {log.es_integro !== undefined && (
                <Alert
                  severity={log.es_integro ? 'success' : 'error'}
                  icon={log.es_integro ? <CheckCircle /> : <Error />}
                >
                  {log.es_integro
                    ? 'El log es íntegro y no ha sido modificado'
                    : 'ADVERTENCIA: El log ha sido modificado o está corrupto'}
                </Alert>
              )}

              {/* Información de compromiso detectado */}
              {log.integridad_comprometida && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Este log fue detectado como comprometido
                  </Alert>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Fecha de Detección
                      </Typography>
                      <Typography variant="body2">
                        {log.fecha_deteccion_compromiso
                          ? formatFechaLog(log.fecha_deteccion_compromiso)
                          : 'No registrada'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">
                        Detectado Por
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {log.detectado_por_nombre || 'No registrado'}
                      </Typography>
                    </Grid>
                    {log.notas_compromiso && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Notas
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          {log.notas_compromiso}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </Paper>

            {/* Cambios Realizados */}
            {log.cambios && Object.keys(log.cambios).length > 0 && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <Code fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Cambios Realizados
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {Object.entries(log.cambios).map(([campo, valor]: [string, any]) => (
                    <Box key={campo} sx={{ mb: 2 }}>
                      <Typography variant="caption" fontWeight="medium" color="primary">
                        {campo}
                      </Typography>
                      <Grid container spacing={1} sx={{ mt: 0.5 }}>
                        <Grid item xs={6}>
                          <Paper variant="outlined" sx={{ p: 1, bgcolor: 'error.50' }}>
                            <Typography variant="caption" color="text.secondary">
                              Anterior:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {valor?.anterior !== undefined ? String(valor.anterior) : 'N/A'}
                            </Typography>
                          </Paper>
                        </Grid>
                        <Grid item xs={6}>
                          <Paper variant="outlined" sx={{ p: 1, bgcolor: 'success.50' }}>
                            <Typography variant="caption" color="text.secondary">
                              Nuevo:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {valor?.nuevo !== undefined ? String(valor.nuevo) : 'N/A'}
                            </Typography>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Box>
              </Paper>
            )}

            {/* Payload (Request Body) */}
            {log.request_data && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  <Code fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Payload (Request Body)
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <JsonViewer
                    value={log.request_data}
                    defaultInspectDepth={2}
                    rootName={false}
                    displayDataTypes={false}
                    displaySize={false}
                  />
                </Box>
              </Paper>
            )}

            {/* Response Data */}
            {log.response_data && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" gutterBottom>
                    <Code fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Response (Respuesta del Servidor)
                  </Typography>
                  <Chip
                    label={log.estado_http}
                    size="small"
                    color={log.estado_http >= 200 && log.estado_http < 300 ? 'success' : 'error'}
                  />
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <JsonViewer
                    value={log.response_data}
                    defaultInspectDepth={2}
                    rootName={false}
                    displayDataTypes={false}
                    displaySize={false}
                  />
                </Box>
              </Paper>
            )}

            {/* User Agent */}
            {log.user_agent && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  User Agent
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', mt: 1 }}>
                  {log.user_agent}
                </Typography>
              </Paper>
            )}
          </Box>
        ) : (
          <Alert severity="error">No se pudo cargar el log</Alert>
        )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
